// ======================================================================
// File: QAInspectionDetail.jsx
// Module: PPE → Quality → Incoming QA
// Purpose: Inspection Detail Screen (Governed Workflow - FINAL)
// ======================================================================

import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import incomingQualityService from "../../services/incomingQualityService";
import QAInspectionDrawer from "./QAInspectionDrawer";

export default function QAInspectionDetail() {

  const { id } = useParams();

  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [selectedLine, setSelectedLine] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);

  // -----------------------------------------------------------
  // LOAD DETAIL
  // -----------------------------------------------------------
  const loadDetail = async () => {
    try {
      setLoading(true);
      const res = await incomingQualityService.getInspectionById(id);
      setInspection(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load inspection");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetail();
  }, [id]);

  // -----------------------------------------------------------
  // GOVERNANCE CHECK
  // -----------------------------------------------------------
  const allLinesEvaluated = useMemo(() => {
    if (!inspection?.lines?.length) return false;

    return inspection.lines.every(line => {
      const received = Number(line.received_qty || 0);
      const accepted = Number(line.accepted_qty || 0);
      const rejected = Number(line.rejected_qty || 0);

      return received === (accepted + rejected);
    });
  }, [inspection]);

  // -----------------------------------------------------------
  // INSPECTION LOCK
  // -----------------------------------------------------------
  const inspectionLocked = useMemo(() => {
    return [
      "ACCEPTED",
      "REJECTED",
      "PARTIAL_ACCEPTED"
    ].includes(inspection?.status_code);
  }, [inspection]);

  // auto close drawer if locked
  useEffect(() => {
    if (inspectionLocked && showDrawer) {
      setShowDrawer(false);
      setSelectedLine(null);
    }
  }, [inspectionLocked, showDrawer]);

  // -----------------------------------------------------------
  // STATUS COLOR
  // -----------------------------------------------------------
  const getStatusBadge = (status) => {
    const map = {
      CREATED: "bg-secondary",
      IN_INSPECTION: "bg-primary",
      ACCEPTED: "bg-success",
      PARTIAL_ACCEPTED: "bg-warning",
      REJECTED: "bg-danger"
    };
    return map[status] || "bg-secondary";
  };

  // -----------------------------------------------------------
  // ACTIONS
  // -----------------------------------------------------------
  const handleStartInspection = async () => {
    try {
      setActionLoading(true);
      await incomingQualityService.startInspection(id);
      toast.success("Inspection started");
      loadDetail();
    } catch (err) {
      toast.error("Unable to start inspection");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteInspection = async () => {

    if (!allLinesEvaluated) {
      toast.warning("All lines must be inspected before completion.");
      return;
    }

    if (!window.confirm("Complete inspection?")) return;

    try {
      setActionLoading(true);
      await incomingQualityService.completeInspection(id);
      toast.success("Inspection completed successfully");
      loadDetail();
    } catch (err) {
      toast.error("Completion failed");
    } finally {
      setActionLoading(false);
    }
  };

  const openInspectionDrawer = (line) => {

    if (inspectionLocked) {
      toast.warning("Inspection already completed.");
      return;
    }

    setSelectedLine(line);
    setShowDrawer(true);
  };

  if (loading || !inspection) {
    return <div style={{ padding: 20 }}>Loading...</div>;
  }

  return (
    <div style={{ padding: 20 }}>

      {/* ================= HEADER ================= */}
      <div className="card mb-3">
        <div className="card-body">

          <h5>Inspection : {inspection.inspection_no}</h5>

          <div className="row mt-3">

            <div className="col-md-3">
              <b>GRN No</b>
              <div>{inspection.grn_no}</div>
            </div>

            <div className="col-md-3">
              <b>Vendor</b>
              <div>{inspection.vendor_name}</div>
            </div>

            <div className="col-md-3">
              <b>Status</b>
              <span className={`badge ${getStatusBadge(inspection.status_code)}`}>
                {inspection.status_code}
              </span>
            </div>

            <div className="col-md-3">
              <b>Inspection Progress</b>
              <div>
                {inspectionLocked ? (
                  <span className="text-success fw-bold">
                    Inspection Completed
                  </span>
                ) : allLinesEvaluated ? (
                  <span className="text-success fw-bold">
                    Ready to Complete
                  </span>
                ) : (
                  <span className="text-warning fw-bold">
                    Pending Lines
                  </span>
                )}
              </div>
            </div>

          </div>

          <div className="mt-3">

            {inspection.status_code === "CREATED" && (
              <button
                className="btn btn-primary me-2"
                disabled={actionLoading}
                onClick={handleStartInspection}
              >
                Start Inspection
              </button>
            )}

            <button
              className="btn btn-success"
              disabled={!allLinesEvaluated || actionLoading || inspectionLocked}
              onClick={handleCompleteInspection}
            >
              Complete Inspection
            </button>

          </div>

        </div>
      </div>

      {/* ================= LINES TABLE ================= */}
      <div className="card">
        <div className="card-body p-0">

          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>Material</th>
                <th>Batch</th>
                <th>Received</th>
                <th>Accepted</th>
                <th>Rejected</th>
                <th>Pending</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {inspection.lines?.map(line => {

                const pending =
                  (line.received_qty || 0)
                  - (line.accepted_qty || 0)
                  - (line.rejected_qty || 0);

                return (
                  <tr key={line.id}>
                    <td>{line.material_name}</td>
                    <td>{line.batch_no}</td>
                    <td>{line.received_qty}</td>
                    <td>{line.accepted_qty}</td>
                    <td>{line.rejected_qty}</td>

                    <td className={pending === 0
                      ? "text-success fw-bold"
                      : "text-warning"}>
                      {pending}
                    </td>

                    <td>
                      <span className={`badge ${getStatusBadge(line.status_code)}`}>
                        {line.status_code}
                      </span>
                    </td>

                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        disabled={inspectionLocked}
                        onClick={() => openInspectionDrawer(line)}
                      >
                        Inspect
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>

          </table>

        </div>
      </div>

      {/* ================= DRAWER ================= */}
      <QAInspectionDrawer
        show={showDrawer}
        line={selectedLine}
        onClose={() => setShowDrawer(false)}
        onSaved={loadDetail}
      />

    </div>
  );
}